import { useState } from "react";
import { MapPin, Eye, Edit, RefreshCw, Bell, Trash2, MoreHorizontal, Check } from "lucide-react";
import { getStatusBadge, formatDate, formatCurrency, revenue } from "./jobConstants";

const GRID = "2.5fr 1.2fr 130px 70px 110px 110px 96px";

const JobsTable = ({
  jobs,
  onViewDetails, onQuickApprove, onEdit, onChangeStatus, onResendNotification, onDelete,
}) => {
  const [openMenuId, setOpenMenuId] = useState(null);

  return (
    <div className="hidden md:block mt-4 sz-card overflow-hidden">
      {/* Header row */}
      <div
        className="grid items-center gap-3 px-[18px] py-[13px] sz-overline text-[#8A90A2]"
        style={{ gridTemplateColumns: GRID, background: "#FAFBFC", borderBottom: "1px solid #EEF0F4" }}
      >
        <div>Job</div>
        <div>Posted By</div>
        <div>Status</div>
        <div>Apps</div>
        <div>Budget</div>
        <div>Revenue</div>
        <div className="text-right">Actions</div>
      </div>

      {jobs.map((job) => {
        const isAdvanced = (job.crew_count > 1) || (job.crew_members?.length > 1);
        const canApprove = job.status === "PENDING_APPROVAL";
        const canNotify = job.status === "SEEKING_APPLICANTS" || job.status === "HIRED";
        const menuOpen = openMenuId === job.id;
        const close = () => setOpenMenuId(null);

        return (
          <div
            key={job.id}
            className="grid items-center gap-3 px-[18px] py-3 hover:bg-[#FAFBFC] transition-colors"
            style={{ gridTemplateColumns: GRID, borderBottom: "1px solid #F1F3F7" }}
          >
            {/* Job */}
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => onViewDetails(job)}
                  className="text-[13.5px] font-semibold text-[#0C1120] truncate text-left hover:text-[#572B7C]"
                  title={job.title}
                >
                  {job.title}
                </button>
                {isAdvanced && (
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-[5px] shrink-0" style={{ fontFamily: "Poppins", color: "#78287C", background: "#EDE4F4" }}>ADV</span>
                )}
              </div>
              <div className="flex items-center gap-2 text-[11.5px] text-[#8A90A2] mt-0.5 truncate">
                <span className="sz-mono text-[#A8AEBC]">JOB-{job.id?.substring(0, 6).toUpperCase()}</span>
                <span className="w-[3px] h-[3px] rounded-full bg-[#CBD0DA] shrink-0" />
                <MapPin className="w-3 h-3 text-[#A8AEBC] shrink-0" />
                <span className="truncate">{job.location?.split(",")[0] || "—"}</span>
                <span className="w-[3px] h-[3px] rounded-full bg-[#CBD0DA] shrink-0" />
                <span className="shrink-0">{formatDate(job.date)}</span>
              </div>
            </div>

            {/* Posted By */}
            <div className="text-[12.5px] text-[#2B3142] truncate" title={job.owner?.name || job.client_name}>
              {job.owner?.name || job.client_name || "—"}
            </div>

            {/* Status */}
            <div>{getStatusBadge(job.status)}</div>

            {/* Apps */}
            <div>
              <span className="text-xs font-semibold text-[#2B3142] px-[9px] py-[3px] rounded-full" style={{ background: "#F1F3F7" }}>
                {job.applications || 0}
              </span>
            </div>

            {/* Budget */}
            <div className="text-[13px] font-semibold text-[#0C1120]" style={{ fontFamily: "Poppins" }}>
              {formatCurrency(job.budget || job.total_amount)}
            </div>

            {/* Revenue */}
            <div className="text-[13px] font-bold text-[#15803D]" style={{ fontFamily: "Poppins" }}>
              {formatCurrency(revenue(job))}
            </div>

            {/* Actions */}
            <div className="flex gap-1.5 justify-end">
              {canApprove && (
                <button
                  onClick={() => onQuickApprove(job)}
                  title="Approve"
                  className="w-[31px] h-[31px] rounded-lg flex items-center justify-center text-white hover:bg-[#28C0D0] hover:text-[#0C1120] transition-colors"
                  style={{ background: "#0C1120" }}
                >
                  <Check className="w-[15px] h-[15px]" strokeWidth={2.4} />
                </button>
              )}
              <button
                onClick={() => onViewDetails(job)}
                title="View"
                className="w-[31px] h-[31px] rounded-lg flex items-center justify-center text-[#2B3142] bg-white hover:text-[#0C1120] transition-colors"
                style={{ border: "1px solid #E0E3EB" }}
              >
                <Eye className="w-[15px] h-[15px]" />
              </button>
              <div className="relative">
                <button
                  onClick={() => setOpenMenuId(menuOpen ? null : job.id)}
                  title="More"
                  className="w-[31px] h-[31px] rounded-lg flex items-center justify-center text-[#2B3142] bg-white hover:text-[#0C1120] transition-colors"
                  style={{ border: "1px solid #E0E3EB" }}
                >
                  <MoreHorizontal className="w-[15px] h-[15px]" />
                </button>
                {menuOpen && (
                  <>
                    <div className="fixed inset-0 z-[35]" onClick={close} />
                    <div className="sz-menu absolute right-0 top-[37px] z-40 p-[5px] min-w-[180px]">
                      <MenuItem onClick={() => { close(); onViewDetails(job); }} icon={<Eye className="w-[15px] h-[15px]" />}>View Details</MenuItem>
                      <MenuItem onClick={() => { close(); onEdit(job); }} icon={<Edit className="w-[15px] h-[15px]" />}>Edit Job</MenuItem>
                      <MenuItem onClick={() => { close(); onChangeStatus(job); }} icon={<RefreshCw className="w-[15px] h-[15px]" />}>Change Status</MenuItem>
                      {canApprove && (
                        <MenuItem onClick={() => { close(); onQuickApprove(job); }} icon={<Check className="w-[15px] h-[15px]" />} tone="green">Approve</MenuItem>
                      )}
                      {canNotify && (
                        <MenuItem onClick={() => { close(); onResendNotification(job); }} icon={<Bell className="w-[15px] h-[15px]" />}>Notifications</MenuItem>
                      )}
                      <div className="h-px bg-[#F1F3F7] my-1 mx-1.5" />
                      <MenuItem onClick={() => { close(); onDelete(job.id, job.title); }} icon={<Trash2 className="w-[15px] h-[15px]" />} tone="red">Delete</MenuItem>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {jobs.length === 0 && (
        <div className="text-center py-12 text-[#8A90A2] text-[13px]">No jobs match your filters.</div>
      )}
    </div>
  );
};

const MenuItem = ({ onClick, icon, children, tone }) => {
  const color = tone === "red" ? "#DC2626" : tone === "green" ? "#15803D" : "#2B3142";
  const hover = tone === "red" ? "hover:bg-[#FEF2F2]" : tone === "green" ? "hover:bg-[#F0FDF4]" : "hover:bg-[#F5F6F9]";
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-[9px] w-full px-[11px] py-[9px] rounded-lg text-left text-[12.5px] font-medium ${hover}`}
      style={{ color }}
    >
      {icon}{children}
    </button>
  );
};

export default JobsTable;
